import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import './question-manager-responsive.css';
import { downloadCSVTemplate, parseCSVFile, submitBulkQuestions } from './questionManagerUtils';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const APTITUDE_QUESTION_API = process.env.REACT_APP_QMANAGER_QUESTION_API || `${API_BASE_URL}/api/admin-app/aptitude-questions/`;
const APTITUDE_TOPICS_API = process.env.REACT_APP_ADMIN_APTITUDE_TOPICS_API || `${API_BASE_URL}/api/admin-app/aptitude-topics/`;
const BULK_UPLOAD_API = process.env.REACT_APP_BULK_UPLOAD_API || `${API_BASE_URL}/api/admin-app/aptitude-questions/bulk/create/`;

const initialQuestionForm = {
    topic: '',
    prompt: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctOption: 'A',
    difficulty: 'Easy',
    marks: 1,
};

const QuestionManagerCreateMain = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const questionId = searchParams.get('id');
    const isEditMode = Boolean(questionId);
    
    // Step tracking
    const [step, setStep] = useState(1);
    const [inputMode, setInputMode] = useState(null);
    
    // Topics
    const [topics, setTopics] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState('');
    const [topicsLoading, setTopicsLoading] = useState(true);
    
    // Single question form
    const [questionForm, setQuestionForm] = useState(initialQuestionForm);
    
    // Bulk upload
    const [bulkFile, setBulkFile] = useState(null);
    const [bulkPreview, setBulkPreview] = useState([]);
    const [bulkErrors, setBulkErrors] = useState([]);
    
    // General state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(isEditMode);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const buildAuthHeaders = () => {
        const token = localStorage.getItem('guest_access_token');
        if (!token) {
            return null;
        }

        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        };
    };

    // Fetch available topics on component mount
    useEffect(() => {
        const fetchTopics = async () => {
            const headers = buildAuthHeaders();
            if (!headers) {
                setTopicsLoading(false);
                return;
            }

            try {
                const response = await fetch(APTITUDE_TOPICS_API, {
                    method: 'GET',
                    headers,
                });

                if (!response.ok) {
                    console.error('Failed to fetch topics');
                    setTopicsLoading(false);
                    return;
                }

                const data = await response.json();
                setTopics(Array.isArray(data) ? data : data.results || []);
            } catch (error) {
                console.error('Error fetching topics:', error);
            } finally {
                setTopicsLoading(false);
            }
        };

        fetchTopics();
    }, []);

    useEffect(() => {
        if (isEditMode && questionId) {
            const fetchQuestion = async () => {
                const headers = buildAuthHeaders();
                if (!headers) {
                    setErrorMessage('Session expired. Please login again.');
                    setIsLoading(false);
                    return;
                }

                try {
                    const response = await fetch(`${APTITUDE_QUESTION_API}${questionId}/`, {
                        method: 'GET',
                        headers,
                    });

                    const data = await response.json();

                    if (!response.ok) {
                        setErrorMessage(data?.detail || data?.error || 'Failed to load question.');
                        setIsLoading(false);
                        return;
                    }

                    setQuestionForm({
                        topic: data.topic || '',
                        prompt: data.prompt || '',
                        optionA: data.option_a || '',
                        optionB: data.option_b || '',
                        optionC: data.option_c || '',
                        optionD: data.option_d || '',
                        correctOption: data.correct_option || 'A',
                        difficulty: data.difficulty || 'Easy',
                        marks: data.marks || 1,
                    });
                } catch (error) {
                    setErrorMessage('Unable to load question right now.');
                } finally {
                    setIsLoading(false);
                }
            };

            fetchQuestion();
        }
    }, [isEditMode, questionId]);

    const handleQuestionFieldChange = (event) => {
        const { name, value } = event.target;
        setQuestionForm((prev) => ({
            ...prev,
            [name]: name === 'marks' ? Number(value) : value,
        }));
    };

    // Handle bulk CSV file upload
    const handleBulkFileChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setBulkFile(file);
        setErrorMessage('');
        
        // Parse the file immediately to show preview
        const result = await parseCSVFile(file);
        
        if (result.errors.length > 0) {
            setBulkErrors(result.errors);
            setBulkPreview([]);
        } else {
            setBulkErrors([]);
            setBulkPreview(result.rows);
        }
    };

    // Submit bulk questions to backend
    const handleBulkSubmit = async () => {
        if (!selectedTopic) {
            setErrorMessage('Please select a topic first');
            return;
        }

        if (bulkPreview.length === 0) {
            setErrorMessage('No valid questions to submit');
            return;
        }

        const headers = buildAuthHeaders();
        if (!headers) {
            setErrorMessage('Session expired. Please login again.');
            return;
        }

        setIsSubmitting(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const result = await submitBulkQuestions(bulkPreview, selectedTopic, headers.Authorization.split(' ')[1]);
            
            if (result.success) {
                setSuccessMessage(`Successfully uploaded ${result.created} questions!${result.failed > 0 ? ` (${result.failed} failed)` : ''}`);
                setBulkFile(null);
                setBulkPreview([]);
                setBulkErrors([]);
                setStep(1);
                setInputMode(null);
                setSelectedTopic('');
                
                setTimeout(() => {
                    navigate('/dashboard/institution/question-manager?role=question-manager');
                }, 2000);
            } else {
                setErrorMessage(result.error || 'Failed to upload questions');
                if (result.details?.failed_questions) {
                    console.error('Failed questions:', result.details.failed_questions);
                }
            }
        } catch (error) {
            setErrorMessage('Error submitting questions: ' + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddQuestion = async () => {
        const topic = (isEditMode ? questionForm.topic : selectedTopic).trim();
        const prompt = questionForm.prompt.trim();
        const optionA = questionForm.optionA.trim();
        const optionB = questionForm.optionB.trim();
        const optionC = questionForm.optionC.trim();
        const optionD = questionForm.optionD.trim();

        if (!topic || !prompt || !optionA || !optionB || !optionC || !optionD) {
            return;
        }

        const headers = buildAuthHeaders();
        if (!headers) {
            setErrorMessage('Session expired. Please login again.');
            setSuccessMessage('');
            return;
        }

        setIsSubmitting(true);
        setErrorMessage('');
        setSuccessMessage('');

        const payload = {
            topic,
            prompt,
            option_a: optionA,
            option_b: optionB,
            option_c: optionC,
            option_d: optionD,
            correct_option: questionForm.correctOption,
            difficulty: questionForm.difficulty,
            status: 'submitted',
            marks: questionForm.marks,
        };

        try {
            const url = isEditMode ? `${APTITUDE_QUESTION_API}${questionId}/` : APTITUDE_QUESTION_API;
            const method = isEditMode ? 'PATCH' : 'POST';

            const response = await fetch(url, {
                method,
                headers,
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (!response.ok) {
                setErrorMessage(data?.detail || data?.error || 'Failed to save question.');
                return;
            }

            setQuestionForm(initialQuestionForm);
            const successMsg = isEditMode ? 'Question updated successfully.' : 'Question saved successfully to aptitude table.';
            setSuccessMessage(successMsg);
            setTimeout(() => {
                navigate('/dashboard/institution/question-manager?role=question-manager');
            }, 1500);
        } catch (error) {
            setErrorMessage('Unable to save question right now.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const isFormIncomplete = (
        !(isEditMode ? questionForm.topic.trim() : selectedTopic.trim())
        || !questionForm.prompt.trim()
        || !questionForm.optionA.trim()
        || !questionForm.optionB.trim()
        || !questionForm.optionC.trim()
        || !questionForm.optionD.trim()
    );

    if (isLoading) {
        return (
            <div className="accordion__area pb---120 question-manager-content">
                <div className="container">
                    <div className="row g-4">
                        <div className="col-lg-12">
                            <div className="text-center py-5">Loading question...</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // STEP 1: Topic Selection
    if (step === 1 && !isEditMode) {
        return (
            <div className="accordion__area pb---120 question-manager-content" style={{ marginTop: '40px' }}>
                <div className="container">
                    <div className="row g-4">
                        {errorMessage && <div className="col-lg-12"><div className="alert alert-danger mb-0" role="alert">{errorMessage}</div></div>}
                        {successMessage && <div className="col-lg-12"><div className="alert alert-success mb-0" role="alert">{successMessage}</div></div>}
                        
                        <div className="col-lg-12">
                            <div className="login-right-form question-manager-entry-card" style={{ height: '100%' }}>
                                <div className="login-top d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div>
                                        <h3>Create Questions - Step 1</h3>
                                        <p>Select a topic to begin creating questions</p>
                                    </div>
                                    <Link to="/dashboard/institution/question-manager?role=question-manager" className="btn btn-outline-primary btn-sm">
                                        Back to Question Bank
                                    </Link>
                                </div>

                                <div className="question-create-form-grid">
                                    <div className="question-create-box question-create-box--full">
                                        <label className="question-create-label">Select Topic *</label>
                                        {topicsLoading ? (
                                            <div className="alert alert-info">Loading topics...</div>
                                        ) : topics.length === 0 ? (
                                            <div className="alert alert-warning">No topics available. Please ask admin to create topics first.</div>
                                        ) : (
                                            <select
                                                value={selectedTopic}
                                                onChange={(e) => setSelectedTopic(e.target.value)}
                                                className="form-select"
                                            >
                                                <option value="">-- Select a Topic --</option>
                                                {topics.map((topic) => (
                                                    <option key={topic.id} value={topic.name}>
                                                        {topic.name}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>

                                    <div className="question-create-box question-create-box--full text-end">
                                        <button
                                            type="button"
                                            className="btn btn-primary question-manager-add-btn"
                                            onClick={() => setStep(2)}
                                            disabled={!selectedTopic || topicsLoading}
                                            style={{
                                                background: '#1e40af',
                                                border: '2px solid #1e3a8a',
                                                padding: '8px 20px',
                                                borderRadius: '8px',
                                                color: '#fff',
                                                fontWeight: '600',
                                                fontSize: '14px',
                                                boxShadow: '0 4px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                                                cursor: !selectedTopic || topicsLoading ? 'not-allowed' : 'pointer',
                                                opacity: !selectedTopic || topicsLoading ? 0.6 : 1
                                            }}
                                        >
                                            Continue to Next Step →
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // STEP 2: Choose input mode (Bulk vs Single)
    if (step === 2 && !isEditMode && !inputMode) {
        return (
            <div className="accordion__area pb---120 question-manager-content" style={{ marginTop: '40px' }}>
                <div className="container">
                    <div className="row g-4">
                        {errorMessage && <div className="col-lg-12"><div className="alert alert-danger mb-0" role="alert">{errorMessage}</div></div>}
                        {successMessage && <div className="col-lg-12"><div className="alert alert-success mb-0" role="alert">{successMessage}</div></div>}
                        
                        <div className="col-lg-12">
                            <div className="login-right-form question-manager-entry-card" style={{ height: '100%' }}>
                                <div className="login-top d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div>
                                        <h3>Create Questions - Step 2</h3>
                                        <p>Topic: <strong>{selectedTopic}</strong> | Choose how to add questions</p>
                                    </div>
                                    <button 
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => { setStep(1); setSelectedTopic(''); }}
                                    >
                                        ← Change Topic
                                    </button>
                                </div>

                                <div className="question-create-form-grid">
                                    <div className="question-create-box question-create-box--full">
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
                                            <div style={{
                                                border: '2px solid #e0e7ff',
                                                borderRadius: '8px',
                                                padding: '20px',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#1e40af'}
                                            onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e0e7ff'}
                                            onClick={() => setInputMode('bulk')}
                                            >
                                                <h5 style={{ marginBottom: '10px' }}>📤 Bulk Upload (Excel)</h5>
                                                <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>Upload multiple questions at once from a CSV file. Faster for adding many questions.</p>
                                                <button className="btn btn-sm btn-primary">Select This Option</button>
                                            </div>

                                            <div style={{
                                                border: '2px solid #e0e7ff',
                                                borderRadius: '8px',
                                                padding: '20px',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#1e40af'}
                                            onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e0e7ff'}
                                            onClick={() => setInputMode('single')}
                                            >
                                                <h5 style={{ marginBottom: '10px' }}>✍️ Single Question</h5>
                                                <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>Enter one question manually. Good for adding specific questions or editing.</p>
                                                <button className="btn btn-sm btn-primary">Select This Option</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // BULK UPLOAD MODE
    if (step === 2 && inputMode === 'bulk' && !isEditMode) {
        return (
            <div className="accordion__area pb---120 question-manager-content" style={{ marginTop: '40px' }}>
                <div className="container">
                    <div className="row g-4">
                        {errorMessage && <div className="col-lg-12"><div className="alert alert-danger mb-0" role="alert">{errorMessage}</div></div>}
                        {successMessage && <div className="col-lg-12"><div className="alert alert-success mb-0" role="alert">{successMessage}</div></div>}
                        
                        <div className="col-lg-12">
                            <div className="login-right-form question-manager-entry-card" style={{ height: '100%' }}>
                                <div className="login-top d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div>
                                        <h3>Bulk Upload Questions</h3>
                                        <p>Topic: <strong>{selectedTopic}</strong> | Upload a CSV file with your questions</p>
                                    </div>
                                    <button 
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setInputMode(null)}
                                    >
                                        ← Back
                                    </button>
                                </div>

                                <div className="question-create-form-grid">
                                    <div className="question-create-box question-create-box--full">
                                        <label className="question-create-label">CSV File Upload</label>
                                        <div style={{
                                            border: '2px dashed #1e40af',
                                            borderRadius: '8px',
                                            padding: '20px',
                                            textAlign: 'center',
                                            background: '#f0f4ff',
                                            cursor: 'pointer'
                                        }}>
                                            <input
                                                type="file"
                                                accept=".csv"
                                                onChange={handleBulkFileChange}
                                                style={{ display: 'none' }}
                                                id="csv-input"
                                            />
                                            <label htmlFor="csv-input" style={{ cursor: 'pointer', display: 'block' }}>
                                                <div style={{ fontSize: '24px', marginBottom: '10px' }}>📄</div>
                                                <div style={{ fontWeight: '600', marginBottom: '5px' }}>
                                                    {bulkFile ? bulkFile.name : 'Click to select CSV file'}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#666' }}>
                                                    or drag and drop here
                                                </div>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="question-create-box question-create-box--full">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={downloadCSVTemplate}
                                            style={{ marginBottom: '10px' }}
                                        >
                                            ⬇️ Download CSV Template
                                        </button>
                                        <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                                            Download and fill in the template with your questions
                                        </p>
                                    </div>

                                    {bulkErrors.length > 0 && (
                                        <div className="col-lg-12">
                                            <div className="alert alert-warning" style={{ marginTop: '10px' }}>
                                                <strong>CSV Errors:</strong>
                                                <ul style={{ marginTop: '5px', marginBottom: '0px' }}>
                                                    {bulkErrors.map((err, idx) => (
                                                        <li key={idx}>{err}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    )}

                                    {bulkPreview.length > 0 && (
                                        <div className="col-lg-12">
                                            <div style={{ marginTop: '15px' }}>
                                                <h6 style={{ marginBottom: '10px' }}>Preview ({bulkPreview.length} questions)</h6>
                                                <div style={{
                                                    overflowX: 'auto',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px'
                                                }}>
                                                    <table style={{
                                                        width: '100%',
                                                        fontSize: '13px',
                                                        borderCollapse: 'collapse'
                                                    }}>
                                                        <thead style={{ background: '#f5f5f5' }}>
                                                            <tr>
                                                                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>#</th>
                                                                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Question</th>
                                                                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Difficulty</th>
                                                                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Marks</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {bulkPreview.slice(0, 5).map((q, idx) => (
                                                                <tr key={idx}>
                                                                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{idx + 1}</td>
                                                                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{q.prompt.substring(0, 50)}...</td>
                                                                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{q.difficulty}</td>
                                                                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{q.marks}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                {bulkPreview.length > 5 && (
                                                    <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                                        ... and {bulkPreview.length - 5} more questions
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div className="question-create-box question-create-box--full text-end">
                                        <button
                                            type="button"
                                            className="btn btn-primary question-manager-add-btn"
                                            onClick={handleBulkSubmit}
                                            disabled={isSubmitting || bulkPreview.length === 0}
                                            style={{
                                                background: '#1e40af',
                                                border: '2px solid #1e3a8a',
                                                padding: '8px 20px',
                                                borderRadius: '8px',
                                                color: '#fff',
                                                fontWeight: '600',
                                                fontSize: '14px',
                                                boxShadow: '0 4px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                                                cursor: isSubmitting || bulkPreview.length === 0 ? 'not-allowed' : 'pointer',
                                                opacity: isSubmitting || bulkPreview.length === 0 ? 0.6 : 1
                                            }}
                                        >
                                            {isSubmitting ? 'Uploading...' : `Upload ${bulkPreview.length} Questions`}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // SINGLE QUESTION MODE (existing form, or EDIT MODE)
    return (
        <div className="accordion__area pb---120 question-manager-content" style={{ marginTop: '40px' }}>
            <div className="container">
                <div className="row g-4">
                    {errorMessage ? (
                        <div className="col-lg-12">
                            <div className="alert alert-danger mb-0" role="alert">{errorMessage}</div>
                        </div>
                    ) : null}

                    {successMessage ? (
                        <div className="col-lg-12">
                            <div className="alert alert-success mb-0" role="alert">{successMessage}</div>
                        </div>
                    ) : null}

                    <div className="col-lg-12">
                        <div className="login-right-form question-manager-entry-card" style={{ height: '100%' }}>
                            <div className="login-top d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div>
                                    <h3>{isEditMode ? 'Edit Aptitude Question' : 'Add Single Question'}</h3>
                                    <p>{isEditMode ? 'Update question details and save changes.' : inputMode === 'single' ? `Adding question to: ${selectedTopic}` : 'Enter question with 4 options and submit to aptitude table.'}</p>
                                </div>
                                <Link to="/dashboard/institution/question-manager?role=question-manager" className="btn btn-outline-primary btn-sm">
                                    Back to Question Bank
                                </Link>
                            </div>

                            <div className="question-create-form-grid">
                                {!isEditMode && (
                                    <div className="question-create-box question-create-box--full">
                                        <label className="question-create-label">Topic</label>
                                        <input
                                            type="text"
                                            value={selectedTopic || questionForm.topic}
                                            disabled
                                            className="form-control"
                                            style={{ background: '#f5f5f5' }}
                                        />
                                    </div>
                                )}

                                {isEditMode && (
                                    <div className="question-create-box">
                                        <label className="question-create-label">Topic</label>
                                        <input
                                            type="text"
                                            name="topic"
                                            value={questionForm.topic}
                                            onChange={handleQuestionFieldChange}
                                            className="form-control"
                                            placeholder="e.g. Quantitative Aptitude"
                                        />
                                    </div>
                                )}

                                <div className="question-create-box question-create-box--full">
                                    <label className="question-create-label">Question</label>
                                    <input
                                        type="text"
                                        name="prompt"
                                        value={questionForm.prompt}
                                        onChange={handleQuestionFieldChange}
                                        className="form-control"
                                        placeholder="Enter aptitude question"
                                    />
                                </div>

                                <div className="question-create-box question-create-box--full">
                                    <label className="question-create-label">Options</label>
                                    <div className="question-create-options-grid">
                                        <input
                                            type="text"
                                            name="optionA"
                                            value={questionForm.optionA}
                                            onChange={handleQuestionFieldChange}
                                            className="form-control"
                                            placeholder="Option A"
                                        />
                                        <input
                                            type="text"
                                            name="optionB"
                                            value={questionForm.optionB}
                                            onChange={handleQuestionFieldChange}
                                            className="form-control"
                                            placeholder="Option B"
                                        />
                                        <input
                                            type="text"
                                            name="optionC"
                                            value={questionForm.optionC}
                                            onChange={handleQuestionFieldChange}
                                            className="form-control"
                                            placeholder="Option C"
                                        />
                                        <input
                                            type="text"
                                            name="optionD"
                                            value={questionForm.optionD}
                                            onChange={handleQuestionFieldChange}
                                            className="form-control"
                                            placeholder="Option D"
                                        />
                                    </div>
                                </div>

                                <div className="question-create-box">
                                    <label className="question-create-label">Correct Option</label>
                                    <select
                                        name="correctOption"
                                        value={questionForm.correctOption}
                                        onChange={handleQuestionFieldChange}
                                        className="form-select"
                                    >
                                        <option value="A">A</option>
                                        <option value="B">B</option>
                                        <option value="C">C</option>
                                        <option value="D">D</option>
                                    </select>
                                </div>

                                <div className="question-create-box">
                                    <label className="question-create-label">Difficulty</label>
                                    <select
                                        name="difficulty"
                                        value={questionForm.difficulty}
                                        onChange={handleQuestionFieldChange}
                                        className="form-select"
                                    >
                                        <option value="Easy">Easy</option>
                                        <option value="Medium">Medium</option>
                                        <option value="Hard">Hard</option>
                                    </select>
                                </div>

                                <div className="question-create-box">
                                    <label className="question-create-label">Marks</label>
                                    <input
                                        type="number"
                                        name="marks"
                                        value={questionForm.marks}
                                        onChange={handleQuestionFieldChange}
                                        min="1"
                                        max="10"
                                        className="form-control"
                                    />
                                </div>

                                <div className="question-create-box question-create-box--full text-end">
                                    <button
                                        type="button"
                                        className="btn btn-primary question-manager-add-btn"
                                        onClick={handleAddQuestion}
                                        disabled={isSubmitting || isFormIncomplete}
                                        style={{
                                            background: '#1e40af',
                                            border: '2px solid #1e3a8a',
                                            padding: '8px 20px',
                                            borderRadius: '8px',
                                            color: '#fff',
                                            fontWeight: '600',
                                            fontSize: '14px',
                                            boxShadow: '0 4px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                                            textDecoration: 'none',
                                            display: 'inline-block',
                                            textTransform: 'none',
                                            cursor: isSubmitting || isFormIncomplete ? 'not-allowed' : 'pointer',
                                            opacity: isSubmitting || isFormIncomplete ? 0.6 : 1
                                        }}
                                    >
                                        {isSubmitting ? 'Saving...' : isEditMode ? 'Update Question' : 'Submit Question'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuestionManagerCreateMain;
